<template>
  <v-layout row justify-center>
    <v-dialog v-model="internalOpen" persistent max-width="520px">
      <v-card>
        <v-card-title class="text-h6 font-weight-bold">
          {{ resolvedTitle }}
        </v-card-title>

        <v-divider />

        <template v-if="success">
          <v-card-text class="py-8">
            <div class="text-h6 text-center font-weight-bold mb-6" color="primary">
              {{ resolvedSuccessMessage }}
            </div>
            <div class="d-flex justify-center">
              <v-btn color="primary" depressed small @click="onDismiss">
                {{ $t('actions.dismiss') }}
              </v-btn>
            </div>
          </v-card-text>
        </template>

        <template v-else>
          <v-card-text class="pt-2 pb-4">
            <v-select
              v-if="showSelect"
              v-model="selectedGroupKey"
              :items="groups"
              :label="$t('settings.architecture.linePricingGroups.dialog.selectLinePricingGroup')"
              item-text="linePricedGroupDescription"
              item-value="linePricingGroupKey"
              :rules="[selectRequired]"
              dense
              outlined
              class="mb-4"
              :disabled="submitting"
            />

            <v-text-field
              v-if="showNameInput"
              :key="`${mode}-${String(selectedGroupKey || '')}`"
              v-model.trim="name"
              :label="$t('settings.architecture.linePricingGroups.dialog.name')"
              :rules="nameRules"
              :error="!!duplicateError"
              :error-messages="duplicateError ? [duplicateError] : []"
              :validate-on-blur="false"
              :lazy-rules="false"
              autofocus
              clearable
              :disabled="submitting"
            />
          </v-card-text>

          <v-divider />

          <v-card-actions class="justify-end">
            <v-btn
              text
              class="text-decoration-underline"
              :disabled="submitting"
              small
              @click="onCancel"
            >
              {{ $t('actions.cancel') }}
            </v-btn>

            <v-btn
              v-if="mode === 'create'"
              color="primary"
              depressed
              :disabled="!isValid || submitting"
              :loading="submitting"
              small
              @click="emitCreate"
            >
              {{ $t('actions.create') }}
            </v-btn>

            <v-btn
              v-else-if="mode === 'edit'"
              color="primary"
              depressed
              :disabled="!isValid || submitting"
              :loading="submitting"
              small
              @click="emitEdit"
            >
              {{ $t('actions.save') }}
            </v-btn>

            <v-btn
              v-else-if="mode === 'delete'"
              color="error"
              depressed
              :disabled="!isValid || submitting"
              :loading="submitting"
              small
              @click="emitDelete"
            >
              {{ $t('actions.delete') }}
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { trim, isEmpty, map } from 'lodash';

export default {
  name: 'LinePricingGroupDialog',

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      default: 'create',
      validator: v => ['create', 'edit', 'delete'].indexOf(v) !== -1,
    },
    groups: {
      type: Array,
      default: () => [],
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    success: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      internalOpen: this.value,
      name: '',
      selectedGroupKey: null,
    };
  },

  computed: {
    resolvedTitle() {
      if (this.mode === 'edit')
        return this.$t('settings.architecture.linePricingGroups.dialog.editLinePricingGroup');
      if (this.mode === 'delete')
        return this.$t('settings.architecture.linePricingGroups.dialog.deleteLinePricingGroup');
      return this.$t('settings.architecture.linePricingGroups.dialog.newLinePricingGroup');
    },

    resolvedSuccessMessage() {
      if (this.mode === 'edit') {
        return this.$t('settings.architecture.linePricingGroups.dialog.linePricingGroupUpdated');
      }
      if (this.mode === 'delete') {
        return this.$t('settings.architecture.linePricingGroups.dialog.linePricingGroupDeleted');
      }
      return this.$t('settings.architecture.linePricingGroups.dialog.lpgCreatedAssignItemsMessage');
    },

    showSelect() {
      return this.mode === 'edit' || this.mode === 'delete';
    },
    showNameInput() {
      return this.mode === 'create' || this.mode === 'edit';
    },

    normalisedGroupNames() {
      return map(this.groups || [], g => this.normalise(g.linePricedGroupDescription));
    },

    nameRules() {
      return [
        v =>
          !isEmpty(trim(v)) ||
          this.$t('settings.architecture.linePricingGroups.dialog.nameRequired'),
      ];
    },

    originalName() {
      if (this.mode !== 'edit' || !this.selectedGroupKey) return '';
      const selectedGroup = (this.groups || []).find(
        g => String(g.linePricingGroupKey) === String(this.selectedGroupKey)
      );
      return selectedGroup ? selectedGroup.linePricedGroupDescription : '';
    },

    isNameChanged() {
      if (this.mode !== 'edit') return true;
      const normalise = s =>
        trim(String(s || ''))
          .replace(/\s+/g, ' ')
          .toLowerCase();
      console.log(this.originalName, this.name);
      return normalise(this.name) !== normalise(this.originalName);
    },

    duplicateError() {
      if (!this.showNameInput) return '';
      const hasName = !isEmpty(trim(this.name));
      if (!hasName) return '';

      // In edit mode, show specific error when name is unchanged
      if (this.mode === 'edit' && !this.isNameChanged) {
        return this.$t('settings.architecture.linePricingGroups.dialog.nameUnchanged');
      }

      return this.isNameUnique(this.name)
        ? ''
        : this.$t('settings.architecture.linePricingGroups.dialog.linePricingGroupNameExists');
    },

    isValid() {
      const hasName = !isEmpty(trim(this.name));
      const unique = this.isNameUnique(this.name);

      if (this.mode === 'create') return hasName && unique;
      if (this.mode === 'edit') {
        // Keep requiring the name to be changed
        return !!this.selectedGroupKey && hasName && unique && this.isNameChanged;
      }
      return !!this.selectedGroupKey;
    },
  },

  watch: {
    value(val) {
      this.internalOpen = val;
      if (val && !this.success) {
        this.resetForm();
      }
    },
    internalOpen(val) {
      if (val !== this.value) {
        this.$emit('input', val);
      }
      if (!val) {
        this.resetForm();
      }
    },
    // If success cleared while open, restore a clean form
    success(val) {
      if (!val && this.internalOpen) {
        this.resetForm();
      }
    },
    selectedGroupKey(newKey) {
      if (this.mode !== 'edit') return;
      const g = (this.groups || []).find(x => String(x.linePricingGroupKey) === String(newKey));
      this.name = g ? g.linePricedGroupDescription : '';
    },
    // Reset name when switching modes
    mode() {
      this.resetForm();
    },
  },

  methods: {
    resetForm() {
      this.name = '';
      this.selectedGroupKey = null;
    },

    nameRequired(val) {
      return (
        !isEmpty(trim(val)) ||
        this.$t('settings.architecture.linePricingGroups.dialog.nameRequired')
      );
    },

    selectRequired(val) {
      return !!val || this.$t('settings.architecture.linePricingGroups.dialog.selectRequired');
    },

    onCancel() {
      this.$emit('cancel');
      this.internalOpen = false;
    },

    onDismiss() {
      this.$emit('dismiss');
      this.internalOpen = false;
    },

    emitCreate() {
      if (!this.isValid) return;
      this.$emit('create', { name: trim(this.name) });
    },

    emitEdit() {
      if (!this.isValid) return;
      this.$emit('edit', {
        linePricingGroupKey: this.selectedGroupKey,
        name: trim(this.name),
      });
    },

    emitDelete() {
      if (!this.isValid) return;
      this.$emit('delete', { linePricingGroupKey: this.selectedGroupKey });
    },

    normalise(v) {
      const t = trim(v || '');
      return t.replace(/\s+/g, ' ').toLowerCase();
    },

    isNameUnique(name) {
      const normalise = s =>
        trim(String(s || ''))
          .replace(/\s+/g, ' ')
          .toLowerCase();
      const target = normalise(name);
      const currentKey = String(this.selectedGroupKey || '');
      return !(this.groups || []).some(g => {
        const sameName = normalise(g.linePricedGroupDescription) === target;
        const isSelf = String(g.linePricingGroupKey || '') === currentKey;
        return sameName && !isSelf;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.v-card-title {
  line-height: 1.2;
}
</style>
